import React, { useState } from "react";
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Paper,
    TextField,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Fab,
    FormControlLabel,
    RadioGroup,
    Radio,
    Chip
} from "@mui/material";
import {
    makeGETRequestOnUserService,
    makePOSTRequestOnUserService,
    makePUTRequestOnUserService,
    makeDELETERequestOnUserServiceWithQueryParameter,
} from "../../Api/api";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit"
import { Download } from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import VerifiedIcon from '@mui/icons-material/Verified';
import { NSDL_STATUS_LABEL, CDSL_STATUS_LABEL } from '../Users/KYCDematDetailsTab';

export const DematDetails = ({ demat_details = [], pan_uuid, fetchKycDocs, currentUser, user_type, setValue, getErrorMessage }: any) => {

    const toastr = require("toastr");

    const safeDematDetails = Array.isArray(demat_details) ? demat_details : [];

    const [open, setOpen] = useState(false);
    const [editData, setEditData] = useState<any>(null);
    const [file, setFile] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [verificationStatus, setVerificationStatus] = useState<{ [key: string]: any }>({});
    const [loadingStatus, setLoadingStatus] = useState<{ [key: string]: boolean }>({});

    const [form, setForm] = useState({
        client_id: "",
        dp_id: "",
        dp_name: "",
        dp_type: "",
        account_status: "Sole",
        pan_2: "",
        pan_3: "",
        password: ""
    });

    const isValidPAN = (pan: string) => /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan);

    const getDPRules = () => {
        if (form.dp_type === "NSDL") {
            return { clientIdLength: 8, dpIdLength: 8, dpIdDisabled: false };
        }
        if (form.dp_type === "CDSL") {
            return { clientIdLength: 16, dpIdLength: 0, dpIdDisabled: true };
        }
        return { clientIdLength: 0, dpIdLength: 0, dpIdDisabled: false };
    };

    const rules = getDPRules();

    const handleChange = (e: any) => {
        const { name, value } = e.target;

        if (name === "client_id" && rules.clientIdLength && value.length > rules.clientIdLength) return;
        if (name === "dp_id" && rules.dpIdLength && value.length > rules.dpIdLength) return;

        setForm({ ...form, [name]: value });
    };

    const resetForm = () => {
        setForm({
            client_id: "",
            dp_id: "",
            dp_name: "",
            dp_type: "",
            account_status: "Sole",
            pan_2: "",
            pan_3: "",
            password: ""
        });
        setFile(null);
        setEditData(null);
    };

    const validateDPDetails = () => {
        if (form.dp_type === "NSDL") {
            if (form.client_id.length !== 8) return toastr.error("Client ID must be 8");
            if (form.dp_id.length !== 8) return toastr.error("DP ID must be 8");
        }

        if (form.dp_type === "CDSL") {
            if (form.client_id.length !== 16) return toastr.error("Client ID must be 16");
        }

        return true;
    };

    const buildPANNumbers = () => {
        let panNumbers: string[] = [];

        if (form.account_status === "Sole") {
            if (!pan_uuid || !isValidPAN(pan_uuid)) {
                toastr.error("Valid PAN required");
                return null;
            }
            panNumbers = [pan_uuid];
        }

        if (form.account_status === "Joint") {
            if (!form.pan_2 || !isValidPAN(form.pan_2)) {
                toastr.error("PAN 2 required");
                return null;
            }

            panNumbers = [pan_uuid, form.pan_2];

            if (form.pan_3) {
                if (!isValidPAN(form.pan_3)) {
                    toastr.error("Invalid PAN 3");
                    return null;
                }
                panNumbers.push(form.pan_3);
            }
        }

        return panNumbers;
    };

    const handleCreateOrUpdate = async () => {
        try {
            setLoading(true);

            if (!validateDPDetails()) return;

            const panNumbers = buildPANNumbers();
            if (!panNumbers) return;

            const fd = new FormData();
            const payloadClientId =
                form.dp_type === "NSDL" ? form.client_id : form.client_id;
            const payloadDpId =
                form.dp_type === "CDSL" ? "undefined" : form.dp_id;
            fd.append("user_type", user_type);
            fd.append("user_id", currentUser.uuid);
            fd.append("client_id", payloadClientId);
            fd.append("dp_id", payloadDpId);
            fd.append("dp_name", form.dp_name);
            fd.append("dp_type", form.dp_type);
            fd.append("account_status", form.account_status);
            fd.append("pan_numbers", panNumbers.join(","));
            fd.append("kyc_doc_plaintext_password", form.password || "");
            console.log(fd)

            if (file) {
                fd.append("file", file);
            }

            let resp;

            if (editData) {
                resp = await makePUTRequestOnUserService(
                    "/partneruser/demat_detail/update/",
                    fd
                );
            } else {
                resp = await makePOSTRequestOnUserService(
                    "/partneruser/demat_detail/create/",
                    fd
                );
            }

            if (resp.status === 200 || resp.status === 201) {
                setOpen(false);
                resetForm();
                toastr.success("Saved Successfully");
                fetchKycDocs();
                setValue((prev: number) => prev + 1);
            }

        } catch (err: any) {
            toastr.error(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const handleDefault = async (client_id: string) => {
        try {
            const resp = await makePUTRequestOnUserService(
                "/partneruser/demat_detail/update_default_demat_details/",
                {
                    user_type,
                    user_id: currentUser.uuid,
                    client_id,
                    is_default: true
                }
            );

            if (resp.status === 200) {
                toastr.success("Set as default");
                fetchKycDocs();
            }
        } catch (err: any) {
            toastr.error(getErrorMessage(err));
        }
    };

    const handleDelete = async (client_id: string) => {
        try {
            const resp =
                await makeDELETERequestOnUserServiceWithQueryParameter(
                    `/partneruser/demat_detail/delete/${user_type}/${currentUser.uuid}/${client_id}/`
                );

            if (resp.status === 204) {
                toastr.success("Deleted");
                fetchKycDocs();
            }
        } catch (err: any) {
            toastr.error(getErrorMessage(err));
        }
    };

    const handleEdit = (item: any) => {
        setEditData(item);
        setForm({
            client_id: item.client_id,
            dp_id: item.dp_id || "",
            dp_name: item.dp_name,
            dp_type: item.dp_type,
            account_status: item.account_status,
            pan_2: item.pan_numbers?.[1] || "",
            pan_3: item.pan_numbers?.[2] || "",
            password: item.kyc_doc_plaintext_password || ""
        });
        setFile(null);
        setOpen(true);
    };

    const handleDownload = (item: any) => {
        try {
            if (!item?.image) {
                toastr.error("No file available");
                return;
            }
            const isPDF =
                item?.image_filename?.toLowerCase().endsWith(".pdf");
            const mimeType = isPDF ? "application/pdf" : "image/jpeg";
            const byteCharacters = atob(item.image);
            const byteNumbers = new Array(byteCharacters.length)
                .fill(0)
                .map((_, i) => byteCharacters.charCodeAt(i));
            const blob = new Blob([new Uint8Array(byteNumbers)], {
                type: mimeType
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = item.image_filename || "document";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            toastr.error("Download failed");
        }
    };

    const handleVerification = async (item: any) => {
        const clientId = item.client_id;
        if (!clientId || !item?.dp_type) {
            toastr.error("Missing client ID or DP type");
            return;
        }
        if (loadingStatus[clientId]) return;
        setLoadingStatus((prev: any) => ({
            ...prev,
            [clientId]: true,
        }));
        let apiUrl = "";
        if (item.dp_type === "NSDL") {
            apiUrl = `/partneruser/nsdl_verification/${user_type}/${clientId}/`;
        } else if (item.dp_type === "CDSL") {
            apiUrl = `/partneruser/cdsl_verification/${user_type}/${clientId}/`;
        } else {
            toastr.error("Invalid DP Type");
            return;
        }
        try {
            const response = await makeGETRequestOnUserService(apiUrl);
            if (response?.status === 200 && response?.data?.dan_response) {
                setVerificationStatus((prev: any) => ({
                    ...prev,
                    [clientId]: response.data,
                }));

                toastr.success("Verification updated");
            } else {
                toastr.error("Invalid verification response");
            }
        } catch (err: any) {
            const msg =
                err?.response?.data?.detail ||
                err?.message ||
                "Verification failed";

            toastr.error(msg);
        } finally {
            setLoadingStatus((prev: any) => ({
                ...prev,
                [clientId]: false,
            }));
        }
    }

    const getVerificationUI = (item: any) => {
        const clientId = item.client_id;
        const dpType = item.dp_type;
        const apiStatus = item?.dan_status_check_response;
        const liveStatus = verificationStatus[clientId]?.dan_response;
        const statusData = apiStatus || liveStatus;

        if (statusData) {
            let statusCode = "";
            let statusLabel = "";
            let isVerified = false;

            if (dpType === "NSDL") {
                statusCode = statusData.status;
                statusLabel = NSDL_STATUS_LABEL[statusCode] || statusCode;
                isVerified = statusCode === "00";
            } else if (dpType === "CDSL") {
                statusCode = statusData.statuscode;
                statusLabel = CDSL_STATUS_LABEL[statusCode] || statusCode;
                isVerified = statusCode === "01";
            }

            return (
                <Tooltip title={`Status: ${statusLabel}`}>
                    {isVerified ? (
                        <VerifiedIcon sx={{ color: "green" }} />
                    ) : (
                        <InfoOutlinedIcon sx={{ color: "orange" }} />
                    )}
                </Tooltip>
            );
        }

        return (
            <Tooltip title="Check verification">
                <IconButton
                    onClick={() => handleVerification(item)}
                    disabled={loadingStatus[clientId]}
                >
                    {loadingStatus[clientId] ? (
                        <CircularProgress size={24} />
                    ) : (
                        <InfoOutlinedIcon />
                    )}
                </IconButton>
            </Tooltip>
        );
    };

    return (
        <Box sx={{ position: "relative", pt: 5 }}>
            {safeDematDetails.length < 5 && (
                <Fab
                    size="small"
                    sx={{
                        position: "absolute",
                        top: 0,
                        right: 10,
                        backgroundColor: "#262349",
                        color: "#fff",
                        width: 36,
                        height: 36,
                        zIndex: 10,
                        "&:hover": { backgroundColor: "#1d1a3a" }
                    }}
                    onClick={() => {
                        resetForm();
                        setOpen(true);
                    }}
                >
                    <AddIcon />
                </Fab>
            )}

            {safeDematDetails.length === 0 && (
                <Box sx={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Typography>No Demat Details Added</Typography>
                </Box>
            )}

            <Grid container spacing={2}>
                {safeDematDetails.map((item: any) => (
                    <Grid item xs={12} sm={6} md={4} key={item.client_id}>
                        <Paper sx={{ p: 2, borderRadius: 3 }}>

                            {item.image ? (
                                item.image_filename?.toLowerCase().endsWith(".pdf") ? (
                                    (() => {
                                        const byteCharacters = atob(item.image);
                                        const byteNumbers = new Array(byteCharacters.length)
                                            .fill(0)
                                            .map((_, i) => byteCharacters.charCodeAt(i));

                                        const blob = new Blob(
                                            [new Uint8Array(byteNumbers)],
                                            { type: "application/pdf" }
                                        );

                                        const url = URL.createObjectURL(blob);

                                        return (
                                            <iframe
                                                src={url}
                                                title="PDF Preview"
                                                style={{
                                                    width: "100%",
                                                    height: 160,
                                                    borderRadius: 8,
                                                    border: "none"
                                                }}
                                            />
                                        );
                                    })()
                                ) : (
                                    <img
                                        src={`data:image/jpeg;base64,${item.image}`}
                                        alt="bank"
                                        style={{
                                            width: "100%",
                                            height: 160,
                                            objectFit: "cover",
                                            borderRadius: 8
                                        }}
                                    />
                                )
                            ) : (
                                <Box
                                    sx={{
                                        height: 160,
                                        background: "#eee",
                                        borderRadius: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center"
                                    }}
                                >
                                    No File
                                </Box>
                            )}

                            <Box mt={1} display="flex" justifyContent="space-between">
                                <Typography fontSize={13}>
                                    Password - {item.kyc_doc_plaintext_password || "NA"}
                                </Typography>
                                {getVerificationUI(item)}
                            </Box>
                            <Typography mt={2} fontSize={13} color="text.secondary">
                                {item.dp_type} - ({item.account_status || "NA"} Account)
                            </Typography>
                            <Typography fontSize={16} fontWeight={600}>
                                {item.dp_name}
                            </Typography>
                            <Typography fontSize={13} color="text.secondary">
                                {item.client_id}
                            </Typography>
                            <Typography mt={2} fontSize={13}>
                                {item.dp_id && item.dp_id !== "undefined" ? item.dp_id : "-"}
                            </Typography>
                            <Box mt={1} display="flex" justifyContent="space-between">
                                <Box>
                                    <IconButton onClick={() => handleEdit(item)}>
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => handleDelete(item.client_id)}>
                                        <DeleteIcon />
                                    </IconButton>
                                    <IconButton onClick={() => handleDownload(item)}>
                                        <Download />
                                    </IconButton>
                                </Box>

                                {!item.is_default ? (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleDefault(item.client_id)}
                                        sx={{ borderColor: "#262349", color: "#262349", borderRadius: "20px" }}
                                    >
                                        Set Default
                                    </Button>
                                ) : (
                                    <Chip label="Default" sx={{ backgroundColor: "green", color: "#fff" }} />
                                )}
                            </Box>

                        </Paper>
                    </Grid>
                ))}
            </Grid>

            <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
                <DialogTitle>{editData ? "Update" : "Create"} Demat Details</DialogTitle>

                <DialogContent>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>DP Type</InputLabel>
                        <Select name="dp_type" value={form.dp_type} onChange={handleChange}>
                            <MenuItem value="NSDL">NSDL</MenuItem>
                            <MenuItem value="CDSL">CDSL</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl sx={{ mb: 2, display: "flex", flexDirection: "row", alignItems: "center", gap: 2 }}>
                        <Typography>Account Status</Typography>
                        <RadioGroup row name="account_status" value={form.account_status} onChange={handleChange}>
                            <FormControlLabel value="Sole" control={<Radio />} label="Sole" />
                            <FormControlLabel value="Joint" control={<Radio />} label="Joint" />
                        </RadioGroup>
                    </FormControl>
                    {form.account_status === "Joint" && (
                        <>
                            <Typography>Please enter all joint holder PAN information below.</Typography>
                            <Grid container spacing={2} mt={1}>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="PAN 2"
                                        name="pan_2"
                                        value={form.pan_2}
                                        onChange={handleChange}
                                        helperText="Required, exactly 10 characters"
                                        error={Boolean(form.pan_2) && !isValidPAN(form.pan_2)}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        label="PAN 3 (Optional)"
                                        name="pan_3"
                                        value={form.pan_3}
                                        onChange={handleChange}
                                        helperText="Optional, must be 10 characters if entered"
                                        error={Boolean(form.pan_3) && !isValidPAN(form.pan_3)}
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}
                    <TextField
                        fullWidth
                        label="Client ID"
                        name="client_id"
                        value={form.client_id}
                        onChange={handleChange}
                        helperText={`Required: ${rules.clientIdLength || "varies"} characters`}
                        error={
                            form.client_id.length > 0 &&
                            rules.clientIdLength > 0 &&
                            form.client_id.length !== rules.clientIdLength
                        }
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        label="DP ID"
                        name="dp_id"
                        value={form.dp_id}
                        onChange={handleChange}
                        disabled={rules.dpIdDisabled}
                        helperText={
                            form.dp_type === "CDSL"
                                ? "Not required for CDSL"
                                : "Required: exactly 8 characters"
                        }
                        error={
                            form.dp_type === "NSDL" &&
                            form.dp_id.length > 0 &&
                            form.dp_id.length !== 8
                        }
                        sx={{ mb: 2 }}
                    />
                    <TextField fullWidth label="DP Name" name="dp_name" value={form.dp_name} onChange={handleChange} sx={{ mb: 2 }} />
                    <TextField fullWidth label="Password" name="password" value={form.password} onChange={handleChange} sx={{ mb: 2 }} />
                    <input
                        type="file"
                        onChange={(e) => setFile(e.target.files?.[0])}
                        style={{ marginBottom: "10px" }}
                    />
                    <Box
                        sx={{
                            height: 160,
                            border: "1px dashed #ccc",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: 2,
                            background: "#fafafa",
                            mb: 2
                        }}
                    >
                        {file ? (
                            file.type === "application/pdf" ? (
                                <iframe
                                    src={URL.createObjectURL(file)}
                                    style={{ width: "100%", height: 160, border: "none" }}
                                />
                            ) : (
                                <img
                                    src={URL.createObjectURL(file)}
                                    style={{ maxWidth: "100%", maxHeight: "100%" }}
                                />
                            )
                        ) : editData?.image ? (
                            editData.image_filename?.toLowerCase().endsWith(".pdf") ? (
                                (() => {
                                    const byteCharacters = atob(editData.image);
                                    const byteNumbers = new Array(byteCharacters.length)
                                        .fill(0)
                                        .map((_, i) => byteCharacters.charCodeAt(i));

                                    const blob = new Blob(
                                        [new Uint8Array(byteNumbers)],
                                        { type: "application/pdf" }
                                    );

                                    const url = URL.createObjectURL(blob);

                                    return (
                                        <iframe
                                            src={url}
                                            style={{ width: "100%", height: 160, border: "none" }}
                                        />
                                    );
                                })()
                            ) : (
                                <img
                                    src={`data:image/jpeg;base64,${editData.image}`}
                                    style={{ maxWidth: "100%", maxHeight: "100%" }}
                                />
                            )
                        ) : (
                            <Typography>Upload Image / PDF</Typography>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{
                        justifyContent: "flex-start",
                        px: 3,
                        pb: 2,
                        gap: 1
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={handleCreateOrUpdate}
                        sx={{
                            backgroundColor: "#262349",
                            "&:hover": {
                                backgroundColor: "#1d1a3a"
                            }
                        }}
                    >
                        {loading ? "Saving..." : "Submit"}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => setOpen(false)}
                        sx={{
                            borderColor: "#262349",
                            color: "#262349",
                            "&:hover": {
                                borderColor: "#1d1a3a",
                                color: "#1d1a3a"
                            }
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};